/**
 * Справочники для генератора тем: нейтральные шкалы (Base Color), акцентные
 * цвета (Theme) и шрифты (Typography).
 *
 * Нейтральные шкалы — стандартные ступени Tailwind. Из них generate.ts собирает
 * фоновые/бордерные/текстовые токены для светлого и тёмного режима.
 */
import type {BaseNeutral} from './types';

/** Ступени нейтральной шкалы, используемые при сборке токенов. */
export interface NeutralScale {
  s50: string;
  s100: string;
  s200: string;
  s300: string;
  s400: string;
  s500: string;
  s700: string;
  s800: string;
  s900: string;
  s950: string;
}

/** Нейтральные палитры (Base Color) — ступени Tailwind. */
export const NEUTRAL_SCALES: Record<BaseNeutral, NeutralScale> = {
  slate: {
    s50: '#f8fafc',
    s100: '#f1f5f9',
    s200: '#e2e8f0',
    s300: '#cbd5e1',
    s400: '#94a3b8',
    s500: '#64748b',
    s700: '#334155',
    s800: '#1e293b',
    s900: '#0f172a',
    s950: '#020617',
  },
  gray: {
    s50: '#f9fafb',
    s100: '#f3f4f6',
    s200: '#e5e7eb',
    s300: '#d1d5db',
    s400: '#9ca3af',
    s500: '#6b7280',
    s700: '#374151',
    s800: '#1f2937',
    s900: '#111827',
    s950: '#030712',
  },
  zinc: {
    s50: '#fafafa',
    s100: '#f4f4f5',
    s200: '#e4e4e7',
    s300: '#d4d4d8',
    s400: '#a1a1aa',
    s500: '#71717a',
    s700: '#3f3f46',
    s800: '#27272a',
    s900: '#18181b',
    s950: '#09090b',
  },
  neutral: {
    s50: '#fafafa',
    s100: '#f5f5f5',
    s200: '#e5e5e5',
    s300: '#d4d4d4',
    s400: '#a3a3a3',
    s500: '#737373',
    s700: '#404040',
    s800: '#262626',
    s900: '#171717',
    s950: '#0a0a0a',
  },
  stone: {
    s50: '#fafaf9',
    s100: '#f5f5f4',
    s200: '#e7e5e4',
    s300: '#d6d3d1',
    s400: '#a8a29e',
    s500: '#78716c',
    s700: '#44403c',
    s800: '#292524',
    s900: '#1c1917',
    s950: '#0c0a09',
  },
};

/** Подпись нейтрали для UI. */
export const NEUTRAL_LABELS: Record<BaseNeutral, string> = {
  slate: 'Slate',
  gray: 'Gray',
  zinc: 'Zinc',
  neutral: 'Neutral',
  stone: 'Stone',
};

/** Опция акцентного цвета (Theme). */
export interface AccentOption {
  id: string;
  label: string;
  hex: string;
}

/** Палитра акцентных цветов (Theme). */
export const ACCENT_OPTIONS: AccentOption[] = [
  {id: 'blue', label: 'Blue', hex: '#1d4ed8'},
  {id: 'indigo', label: 'Indigo', hex: '#4f46e5'},
  {id: 'violet', label: 'Violet', hex: '#7c3aed'},
  {id: 'sky', label: 'Sky', hex: '#0284c7'},
  {id: 'cyan', label: 'Cyan', hex: '#0891b2'},
  {id: 'teal', label: 'Teal', hex: '#0d9488'},
  {id: 'emerald', label: 'Emerald', hex: '#059669'},
  {id: 'green', label: 'Green', hex: '#16a34a'},
  {id: 'amber', label: 'Amber', hex: '#d97706'},
  {id: 'orange', label: 'Orange', hex: '#ea580c'},
  {id: 'red', label: 'Red', hex: '#dc2626'},
  {id: 'rose', label: 'Rose', hex: '#e11d48'},
  {id: 'pink', label: 'Pink', hex: '#db2777'},
  {id: 'fuchsia', label: 'Fuchsia', hex: '#c026d3'},
  {id: 'slate', label: 'Slate', hex: '#475569'},
];

/** Опция шрифта (Typography). `google` — query для fonts.googleapis.com. */
export interface FontOption {
  label: string;
  /** CSS font-family stack (значение, сохраняемое в теме). `''` — дефолт. */
  family: string;
  /** Имя семейства в Google Fonts (`Family:wght@...`); пусто — не грузить. */
  google?: string;
}

/** Опция «по умолчанию» (Inter из index.html). */
export const DEFAULT_FONT_OPTION: FontOption = {
  label: 'По умолчанию (Inter)',
  family: '',
};

/** Шрифты для заголовков и текста (sans + serif). */
export const TEXT_FONTS: FontOption[] = [
  DEFAULT_FONT_OPTION,
  {
    label: 'Inter',
    family: "'Inter', sans-serif",
    google: 'Inter:wght@400;500;600;700',
  },
  {
    label: 'Geist',
    family: "'Geist', sans-serif",
    google: 'Geist:wght@400;500;600;700',
  },
  {
    label: 'Roboto',
    family: "'Roboto', sans-serif",
    google: 'Roboto:wght@400;500;700',
  },
  {
    label: 'Open Sans',
    family: "'Open Sans', sans-serif",
    google: 'Open+Sans:wght@400;500;600;700',
  },
  {
    label: 'Montserrat',
    family: "'Montserrat', sans-serif",
    google: 'Montserrat:wght@400;500;600;700',
  },
  {
    label: 'Manrope',
    family: "'Manrope', sans-serif",
    google: 'Manrope:wght@400;500;600;700',
  },
  {
    label: 'Nunito Sans',
    family: "'Nunito Sans', sans-serif",
    google: 'Nunito+Sans:wght@400;500;600;700',
  },
  {
    label: 'Lora (serif)',
    family: "'Lora', serif",
    google: 'Lora:wght@400;500;600;700',
  },
  {
    label: 'Source Serif 4 (serif)',
    family: "'Source Serif 4', serif",
    google: 'Source+Serif+4:wght@400;500;600;700',
  },
  {
    label: 'Merriweather (serif)',
    family: "'Merriweather', serif",
    google: 'Merriweather:wght@400;700',
  },
];

/** Моноширинные шрифты (код/терминал). */
export const MONO_FONTS: FontOption[] = [
  DEFAULT_FONT_OPTION,
  {
    label: 'JetBrains Mono',
    family: "'JetBrains Mono', monospace",
    google: 'JetBrains+Mono:wght@400;500;600;700',
  },
  {
    label: 'Fira Code',
    family: "'Fira Code', monospace",
    google: 'Fira+Code:wght@400;500;600;700',
  },
  {
    label: 'IBM Plex Mono',
    family: "'IBM Plex Mono', monospace",
    google: 'IBM+Plex+Mono:wght@400;500;600;700',
  },
  {
    label: 'Source Code Pro',
    family: "'Source Code Pro', monospace",
    google: 'Source+Code+Pro:wght@400;500;600;700',
  },
  {
    label: 'Geist Mono',
    family: "'Geist Mono', monospace",
    google: 'Geist+Mono:wght@400;500;600;700',
  },
];

/** Все известные шрифты (для подгрузки Google Fonts по сохранённому family). */
export const ALL_FONTS: FontOption[] = [...TEXT_FONTS, ...MONO_FONTS];

/** Найти Google-query по сохранённому CSS-family. */
export const findGoogleQuery = (
  family: string | null | undefined,
): string | undefined =>
  family ? ALL_FONTS.find((f) => f.family === family)?.google : undefined;

/** Радиус-пресеты (Style/Radius). */
export const RADIUS_OPTIONS: {label: string; value: number}[] = [
  {label: 'Без скругления', value: 0},
  {label: 'Малый', value: 0.3},
  {label: 'Средний (по умолчанию)', value: 0.5},
  {label: 'Большой', value: 0.75},
  {label: 'Очень большой', value: 1},
];

/** Подписи уровней теней. */
export const SHADOW_LABELS: Record<string, string> = {
  none: 'Без теней',
  sm: 'Лёгкие',
  md: 'Средние',
  lg: 'Заметные',
  xl: 'Сильные',
};
